/**
 * CompanyFormDialog Component
 * 
 * Modal dialog for creating/editing companies
 * Includes validation and form handling
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TextField, TextAreaField, SelectField, FormSection } from '@/components/ui/form-field';
import { SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import type { Company, CreateCompanyRequest, UpdateCompanyRequest, CompanyStatus } from '@/types';

const companySchema = z.object({
  name: z.string().min(2, 'Company name must be at least 2 characters'),
  code: z
    .string()
    .min(2, 'Company code must be at least 2 characters')
    .max(20, 'Company code must be at most 20 characters')
    .regex(/^[A-Z0-9-]+$/, 'Company code must be uppercase letters, numbers, and hyphens only'),
  description: z.string().optional(),
  status: z.enum(['active', 'inactive', 'setup']),
});

type CompanyFormData = z.infer<typeof companySchema>;

interface CompanyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company?: Company | null;
  onSubmit: (data: CreateCompanyRequest | UpdateCompanyRequest) => Promise<void>;
  onValidateCode?: (code: string, excludeId?: string) => Promise<boolean>;
}

export function CompanyFormDialog({
  open,
  onOpenChange,
  company,
  onSubmit,
  onValidateCode,
}: CompanyFormDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!company;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    setError,
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      status: 'setup',
    },
  });

  const statusValue = watch('status');

  // Reset form when dialog opens/closes or company changes
  useEffect(() => {
    if (open) {
      if (company) {
        reset({
          name: company.name,
          code: company.code,
          description: company.description || '',
          status: company.status,
        });
      } else {
        reset({
          name: '',
          code: '',
          description: '',
          status: 'setup',
        });
      }
    }
  }, [open, company, reset]);

  const handleFormSubmit = async (data: CompanyFormData) => {
    setIsSubmitting(true);

    try {
      // Validate company code uniqueness if provided
      if (onValidateCode && data.code !== company?.code) {
        const isAvailable = await onValidateCode(data.code, company?.id);
        if (!isAvailable) {
          setError('code', {
            type: 'manual',
            message: 'This company code is already in use',
          });
          setIsSubmitting(false);
          return;
        }
      }

      await onSubmit(data);
      toast.success(isEditing ? 'Company updated successfully' : 'Company created successfully');
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting company form:', error);
      toast.error(
        isEditing ? 'Failed to update company' : 'Failed to create company'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Company' : 'Create New Company'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update company details and settings'
              : 'Add a new company to your organization'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          <FormSection>
            <TextField
              label="Company Name"
              width="lg"
              placeholder="e.g., UK Operations"
              required
              {...register('name')}
              error={errors.name?.message}
              disabled={isSubmitting}
            />

            <TextField
              label="Company Code"
              width="sm"
              placeholder="e.g., UK-OPS"
              hint="Unique identifier (uppercase letters, numbers, and hyphens only)"
              required
              {...register('code')}
              error={errors.code?.message}
              disabled={isSubmitting}
              onChange={(e) => {
                // Auto-uppercase
                const value = e.target.value.toUpperCase();
                setValue('code', value);
              }}
            />

            <TextAreaField
              label="Description"
              width="full"
              placeholder="Optional description of this company"
              rows={3}
              {...register('description')}
              error={errors.description?.message}
              disabled={isSubmitting}
            />

            <SelectField
              label="Status"
              width="dropdown_sm"
              placeholder="Select status"
              value={statusValue}
              onValueChange={(value) => setValue('status', value as CompanyStatus)}
              error={errors.status?.message}
              disabled={isSubmitting}
            >
              <SelectItem value="setup">Setup (Initial configuration)</SelectItem>
              <SelectItem value="active">Active (Fully operational)</SelectItem>
              <SelectItem value="inactive">Inactive (Temporarily disabled)</SelectItem>
            </SelectField>
          </FormSection>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditing ? 'Update Company' : 'Create Company'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
